/**
 * One timeline for a project (RFC 0313 3i). PURE.
 *
 * It holds nothing of its own. Every row is read from something the project
 * already has, a document's change graph or the session registry, and names
 * the thing it opens. There is no undo here: an undo belongs next to the change,
 * where you can see what it would put back, so a row takes you there.
 */

import type {
  DocumentRef,
  HistoryChange,
  SessionRef,
  Version,
} from '@mod/core/model'
import type {
  CodeCommit,
  CodeFileChange,
} from '../../services/code/codeHistory'

export type TimelineFilter =
  | 'all'
  | 'documents'
  | 'sessions'
  | 'code'
  | 'versions'

export type TimelineRow =
  | {
      kind: 'document'
      key: string
      /** Millis, the newest change in the group. */
      at: number
      documentId: string
      title: string
      /** Who, as author keys: `s:<sessionId>` or `p:<personId>`. */
      who: string[]
      changes: number
    }
  | {
      kind: 'session'
      key: string
      at: number
      sessionId: string
      title: string
      finished: boolean
    }
  | {
      kind: 'version'
      key: string
      at: number
      version: Version
      /** How many documents it holds. */
      documents: number
    }
  | {
      /** A commit on one of the checkout's branches (3n). */
      kind: 'commit'
      key: string
      at: number
      commit: CodeCommit
    }
  | {
      /** What the checkout has changed and not committed, as of now. */
      kind: 'working'
      key: string
      at: number
      files: CodeFileChange[]
    }

/** How far back the timeline reads a document, by its catalog `updatedAt`. */
export const WINDOW_MS = 14 * 86_400_000

/** At most this many documents are opened to read their changes. */
export const DOCUMENT_CAP = 20

/** The documents worth opening: changed in the window, newest first. */
export function recentDocuments(
  docs: DocumentRef[],
  now: number,
): DocumentRef[] {
  return docs
    .filter((d) => now - d.updatedAt <= WINDOW_MS)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, DOCUMENT_CAP)
}

const whoOf = (r: HistoryChange) =>
  r.sessionId ? `s:${r.sessionId}` : r.authorId ? `p:${r.authorId}` : 'none'

export function buildTimeline(input: {
  documents: DocumentRef[]
  /** Each opened document's rows, newest first. */
  rows: Map<string, HistoryChange[]>
  sessions: SessionRef[]
  /** The project's versions (3c); every one is a row, however old. */
  versions?: Version[]
  /** The checkout's recent commits (3n), newest first. */
  commits?: CodeCommit[]
  /** Its uncommitted files. */
  working?: CodeFileChange[]
  now: number
  filter?: TimelineFilter
}): TimelineRow[] {
  const filter = input.filter ?? 'all'
  const out: TimelineRow[] = []
  if (filter === 'all' || filter === 'versions') {
    for (const v of input.versions ?? []) {
      out.push({
        kind: 'version',
        key: `v:${v.id}`,
        at: v.at,
        version: v,
        documents: Object.keys(v.heads).length,
      })
    }
  }
  if (filter === 'all' || filter === 'documents') {
    for (const d of input.documents) {
      const rows = (input.rows.get(d.id) ?? []).filter(
        (r) => input.now - r.time * 1000 <= WINDOW_MS,
      )
      if (!rows.length) continue
      // One row per document per day: a morning of edits is one line.
      const byDay = new Map<string, HistoryChange[]>()
      for (const r of rows) {
        const day = new Date(r.time * 1000).toDateString()
        byDay.set(day, [...(byDay.get(day) ?? []), r])
      }
      for (const [day, group] of byDay) {
        out.push({
          kind: 'document',
          key: `d:${d.id}:${day}`,
          at: Math.max(...group.map((r) => r.time)) * 1000,
          documentId: d.id,
          title: d.title,
          who: [...new Set(group.map(whoOf))],
          changes: group.reduce((n, r) => n + r.hashes.length, 0),
        })
      }
    }
  }
  if (filter === 'all' || filter === 'sessions') {
    for (const s of input.sessions) {
      const at = s.completedAt ?? s.updatedAt
      if (input.now - at > WINDOW_MS) continue
      out.push({
        kind: 'session',
        key: `s:${s.id}`,
        at,
        sessionId: s.id,
        title: s.title,
        finished: s.completedAt !== undefined,
      })
    }
  }
  if (filter === 'all' || filter === 'code') {
    if (input.working?.length) {
      out.push({
        kind: 'working',
        key: 'w',
        at: input.now,
        files: input.working,
      })
    }
    for (const c of input.commits ?? []) {
      const at = Date.parse(c.date)
      if (!Number.isFinite(at) || input.now - at > WINDOW_MS) continue
      // Named by branch, not by session: a branch name says nothing about
      // which session made a commit on it, and main would credit them all to
      // whoever last worked there. A session's own Changes lists its commits.
      out.push({ kind: 'commit', key: `c:${c.sha}`, at, commit: c })
    }
  }
  return out.sort((a, b) => b.at - a.at)
}
